import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Plus, Image as ImageIcon } from "lucide-react";
import { ALLERGENS_DATA } from "@/components/menu/allergensData";
import { API_URL } from "@/utils/apiConfig";
import { ProductShareLink } from "@/components/menu/ProductShareLink";

export default function ProductDetailsForm({
  name, setName, nameEn, setNameEn, nameEs, setNameEs,
  price, setPrice, originalPrice, setOriginalPrice, discountPct, setDiscountPct,
  code, setCode, categoryId, setCategoryId, localCategories,
  setNewCategoryPosition, setShowQuickCategoryModal,
  imageUrl, setImageUrl, videoUrl, setVideoUrl, mediaType, setMediaType,
  description, setDescription, quantity, setQuantity, prepTime, setPrepTime,
  availability, setAvailability, allergens, toggleAllergen,
  uploading, uploadingPos, position, setPosition,
  showInDelivery, setShowInDelivery, showInTable, setShowInTable, showInPos, setShowInPos,
  priceTable, setPriceTable, posMediaUrl, setPosMediaUrl, posMediaType, setPosMediaType,
  publicDisplayOnly, setPublicDisplayOnly,
  requiresBuffetRules, setRequiresBuffetRules,
  loading, handleSubmit, handleMediaUpload, handlePosMediaUpload, handleDrop,
  fileInputRef, posFileInputRef, allowVideo, product, menuLinkBase, onClose,
}) {
  return (
    <form onSubmit={handleSubmit} className="space-y-3 mt-2 pr-2">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Nombre *</Label>
          <Input value={name} onChange={e => setName(e.target.value)} required className="mt-1" data-testid="product-name" />
          {product?.id && name.trim() && menuLinkBase && (
            <ProductShareLink variant="inline" product={{ id: product.id, name: name.trim() }}
              origin={menuLinkBase.origin} restaurantSlug={menuLinkBase.restaurantSlug} />
          )}
        </div>
        <div><Label>Codigo</Label><Input value={code} onChange={e => setCode(e.target.value)} className="mt-1" data-testid="product-code" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="flex items-center gap-1">🇬🇧 Nome (EN)</Label><Input value={nameEn} onChange={e => setNameEn(e.target.value)} placeholder="English name" className="mt-1" /></div>
        <div><Label className="flex items-center gap-1">🇪🇸 Nombre (ES)</Label><Input value={nameEs} onChange={e => setNameEs(e.target.value)} placeholder="Nombre en español" className="mt-1" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Precio *</Label><Input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} required className="mt-1" data-testid="product-price" /></div>
        <div>
          <Label>Categoria</Label>
          <div className="flex gap-1 mt-1">
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className="flex-1" data-testid="product-category"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
              <SelectContent>{localCategories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button type="button" variant="outline" size="icon"
              className="shrink-0 border-emerald-300 text-emerald-600 hover:bg-emerald-50"
              onClick={() => { setNewCategoryPosition(localCategories.length); setShowQuickCategoryModal(true); }}
              title="Crear nueva categoria" data-testid="quick-create-category-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Preco Original <span className="text-gray-400 text-[10px]">(antes do desconto)</span></Label>
          <Input type="number" step="0.01" value={originalPrice}
            onChange={e => {
              setOriginalPrice(e.target.value);
              if (e.target.value && price) {
                const pct = Math.round((1 - parseFloat(price) / parseFloat(e.target.value)) * 100);
                if (pct > 0 && pct < 100) setDiscountPct(pct);
              }
            }}
            placeholder="0.00" className="mt-1" data-testid="product-original-price" />
        </div>
        <div><Label>Desconto % <span className="text-gray-400 text-[10px]">(badge visivel)</span></Label><Input type="number" step="1" min="0" max="99" value={discountPct} onChange={e => setDiscountPct(e.target.value)} placeholder="0" className="mt-1" data-testid="product-discount-pct" /></div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div><Label>Cantidad</Label><Input type="number" value={quantity} onChange={e => setQuantity(e.target.value)} className="mt-1" /></div>
        <div><Label>Prep. (min)</Label><Input type="number" value={prepTime} onChange={e => setPrepTime(e.target.value)} className="mt-1" /></div>
        <div><Label>Posicion</Label><Input type="number" min={0} value={position} onChange={e => setPosition(e.target.value)} className="mt-1" data-testid="product-position" /><p className="text-xs text-gray-400 mt-1">0 = primero</p></div>
      </div>
      <div>
        <Label>Disponibilidad</Label>
        <Select value={availability} onValueChange={setAvailability}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos (Mesa + Delivery)</SelectItem>
            <SelectItem value="table_only">Solo Mesa</SelectItem>
            <SelectItem value="delivery_only">Solo Delivery</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="bg-gray-50 rounded-lg p-3 space-y-3">
        <Label className="text-sm font-semibold text-gray-700">Visibilidad por Canal</Label>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={showInDelivery} onChange={e => setShowInDelivery(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" data-testid="show-in-delivery" />
            <span className="text-sm">Mostrar en Delivery/Menu Publico</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={showInTable} onChange={e => setShowInTable(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" data-testid="show-in-table" />
            <span className="text-sm">Mostrar en Mesa (QR)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={showInPos} onChange={e => setShowInPos(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500" data-testid="show-in-pos" />
            <span className="text-sm">Mostrar en POS</span>
          </label>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={publicDisplayOnly} onChange={e => setPublicDisplayOnly(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500" data-testid="public-display-only" />
          <span className="text-sm">Mostrar no menu público, mas bloquear compra online</span>
        </label>
        <p className="text-xs text-gray-400 ml-6 -mt-1">Use para combos de marketing ou promoções disponíveis apenas para consumo no local.</p>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={requiresBuffetRules} onChange={e => setRequiresBuffetRules(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500" data-testid="requires-buffet-rules" />
          <span className="text-sm">Exigir aceite das normas do buffet (Mesa/QR)</span>
        </label>
        <p className="text-xs text-gray-400 ml-6 -mt-1">O cliente na mesa deve ler e aceitar as normas do buffet antes de pedir este produto. Outros produtos não são afetados.</p>
        {showInTable && (
          <div>
            <Label className="text-xs text-gray-500">Precio diferenciado para Mesa (opcional)</Label>
            <Input type="number" step="0.01" value={priceTable} onChange={e => setPriceTable(e.target.value)} placeholder={`Mismo precio: ${price || '0.00'} EUR`} className="mt-1 text-sm" data-testid="price-table" />
          </div>
        )}
      </div>
      <div className="bg-blue-50 rounded-lg p-3 space-y-2">
        <Label className="text-sm font-semibold text-blue-700">Midia Interna POS <span className="text-xs font-normal text-blue-500">(nao aparece no menu publico)</span></Label>
        <p className="text-xs text-blue-500">Foto ou video para identificacao visual do produto no POS.</p>
        <div className="border-2 border-dashed border-blue-200 rounded-lg p-3 text-center cursor-pointer hover:border-blue-400 transition relative"
          onClick={() => !uploadingPos && posFileInputRef.current?.click()}
          data-testid="pos-media-upload-zone">
          <input ref={posFileInputRef} type="file" accept="image/*,video/*" className="hidden"
            onClick={e => { e.target.value = null; }}
            onChange={e => e.target.files?.[0] && handlePosMediaUpload(e.target.files[0])} />
          {uploadingPos ? (
            <div className="flex items-center justify-center gap-2 py-3">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              <span className="text-sm text-gray-500">Subindo...</span>
            </div>
          ) : posMediaUrl ? (
            <div className="relative group">
              {posMediaType === "video"
                ? <video src={posMediaUrl.startsWith("/api/") ? `${API_URL}${posMediaUrl}` : posMediaUrl} className="w-full h-24 object-cover rounded-md" muted autoPlay playsInline loop />
                : <img src={posMediaUrl.startsWith("/api/") ? `${API_URL}${posMediaUrl}` : posMediaUrl} alt="POS" className="w-full h-24 object-cover rounded-md" />
              }
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition rounded-md flex items-center justify-center">
                <span className="text-white text-xs font-medium">Click para cambiar</span>
              </div>
              <button type="button" onClick={e => { e.stopPropagation(); setPosMediaUrl(""); setPosMediaType("image"); }} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">x</button>
              {posMediaType === "video" && <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">VIDEO</span>}
            </div>
          ) : (
            <div className="py-3">
              <ImageIcon className="w-6 h-6 mx-auto text-blue-300 mb-1" />
              <p className="text-xs text-blue-500">Click para adicionar midia POS</p>
              <p className="text-[10px] text-blue-400">Foto ou video para uso interno</p>
            </div>
          )}
        </div>
      </div>
      <div>
        <Label>Foto{allowVideo ? " o Video" : ""} del Producto</Label>
        <div className="mt-1 border-2 border-dashed border-gray-200 rounded-lg p-3 text-center cursor-pointer hover:border-emerald-400 transition relative"
          onClick={() => !uploading && fileInputRef.current?.click()}
          onDrop={handleDrop} onDragOver={e => e.preventDefault()}
          data-testid="image-upload-zone">
          <input ref={fileInputRef} type="file" accept={allowVideo ? "image/*,video/*" : "image/*"} className="hidden"
            onClick={e => { e.target.value = null; }}
            onChange={e => e.target.files?.[0] && handleMediaUpload(e.target.files[0])} data-testid="image-file-input" />
          {uploading ? (
            <div className="flex items-center justify-center gap-2 py-4"><Loader2 className="w-5 h-5 animate-spin text-emerald-600" /><span className="text-sm text-gray-500">Subiendo...</span></div>
          ) : mediaType === "video" && videoUrl ? (
            <div className="relative group">
              <video src={videoUrl.startsWith("/api/") ? `${API_URL}${videoUrl}` : videoUrl} className="w-full h-32 object-cover rounded-md" muted autoPlay playsInline loop />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition rounded-md flex items-center justify-center"><span className="text-white text-xs font-medium">Click para cambiar</span></div>
              <button type="button" onClick={e => { e.stopPropagation(); setVideoUrl(""); setMediaType("image"); }} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">x</button>
              <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">VIDEO</span>
            </div>
          ) : imageUrl ? (
            <div className="relative group">
              <img src={imageUrl.startsWith("/api/") ? `${API_URL}${imageUrl}` : imageUrl} alt="Producto" className="w-full h-32 object-cover rounded-md" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition rounded-md flex items-center justify-center"><span className="text-white text-xs font-medium">Click para cambiar</span></div>
              <button type="button" onClick={e => { e.stopPropagation(); setImageUrl(""); }} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs" data-testid="remove-image-btn">x</button>
            </div>
          ) : (
            <div className="py-4">
              <ImageIcon className="w-8 h-8 mx-auto text-gray-300 mb-1" />
              <p className="text-xs text-gray-400">{allowVideo ? "Click o arrastra imagen/video aqui" : "Click o arrastra imagen aqui"}</p>
              <p className="text-xs text-gray-300">Imagen: max 5MB{allowVideo ? " - Video: max 50MB" : ""}</p>
              <p className="text-xs text-gray-300">{allowVideo ? "Formatos: JPG, PNG, MP4, MOV, WebM" : "Formatos: JPG, PNG, WebP"}</p>
              {!allowVideo && <p className="text-xs text-amber-500 mt-1">Videos disponibles en el plan Premium (189 EUR/mes)</p>}
            </div>
          )}
        </div>
        <div className="mt-2 flex gap-2">
          <Input placeholder="O pega URL de video (YouTube, Instagram, etc.)" className="text-xs"
            value={mediaType === "video" ? videoUrl : imageUrl}
            onChange={e => {
              const url = e.target.value;
              if (url.match(/\.(mp4|webm|mov|avi)/i) || url.includes("youtube") || url.includes("youtu.be") || url.includes("instagram") || url.includes("vimeo")) {
                setVideoUrl(url); setImageUrl(""); setMediaType("video");
              } else {
                setImageUrl(url); setVideoUrl(""); setMediaType("image");
              }
            }}
            data-testid="media-url-input" />
        </div>
      </div>
      <div><Label>Descripcion</Label><Textarea value={description} onChange={e => setDescription(e.target.value)} className="mt-1" rows={2} /></div>
      <div>
        <Label className="mb-2 block">Alergenos</Label>
        <div className="flex flex-wrap gap-2" data-testid="allergen-selector">
          {ALLERGENS_DATA.map(a => (
            <button key={a.id} type="button"
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium border transition-all ${allergens.includes(a.id) ? "bg-red-50 border-red-300 text-red-700" : "bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300"}`}
              onClick={() => toggleAllergen(a.id)} data-testid={`allergen-${a.id}`}>
              <span className="text-sm">{a.icon}</span>{a.name}
            </button>
          ))}
        </div>
        {allergens.length > 0 && <p className="text-xs text-gray-500 mt-1">{allergens.length} alergeno(s)</p>}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
        <Button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-product-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
        </Button>
      </DialogFooter>
    </form>
  );
}
